import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from './Button';

export interface EnhancedEmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  illustration?: 'pattern' | 'gradient' | 'none';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Enhanced Empty State Component
 * Polished empty states with optional background patterns
 */
export const EnhancedEmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  illustration = 'pattern',
  size = 'md',
}: EnhancedEmptyStateProps) => {
  const sizeClasses = {
    sm: {
      container: 'py-8 px-4',
      icon: 'w-12 h-12',
      iconBg: 'w-20 h-20',
      title: 'text-base',
      description: 'text-sm',
    },
    md: {
      container: 'py-12 px-4',
      icon: 'w-16 h-16',
      iconBg: 'w-24 h-24',
      title: 'text-lg',
      description: 'text-sm',
    },
    lg: {
      container: 'py-16 px-6',
      icon: 'w-20 h-20',
      iconBg: 'w-28 h-28',
      title: 'text-xl',
      description: 'text-base',
    },
  };

  const classes = sizeClasses[size];

  const getBackgroundStyle = () => {
    switch (illustration) {
      case 'pattern':
        return {
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        };
      case 'gradient':
        return {
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
        };
      default:
        return {};
    }
  };

  return (
    <div 
      className={`flex flex-col items-center justify-center text-center ${classes.container} rounded-xl`}
      style={getBackgroundStyle()}
    >
      {Icon && (
        <div className={`${classes.iconBg} bg-gray-100 rounded-full flex items-center justify-center mb-4 relative overflow-hidden`}>
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-transparent" />
          <Icon className={`${classes.icon} text-gray-400 relative z-10`} />
        </div>
      )}
      
      <h3 className={`${classes.title} font-semibold text-gray-900 mb-2`}>
        {title}
      </h3>
      
      {description && (
        <p className={`${classes.description} text-gray-600 max-w-sm mb-6 leading-relaxed`}>
          {description}
        </p>
      )}
      
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <Button
              onClick={action.onClick}
              leftIcon={action.icon}
              size={size === 'lg' ? 'lg' : 'md'}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outline"
              onClick={secondaryAction.onClick}
              leftIcon={secondaryAction.icon}
              size={size === 'lg' ? 'lg' : 'md'}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact Empty State for smaller spaces
 */
export const CompactEmptyState = ({
  icon: Icon,
  message,
  action,
}: {
  icon?: LucideIcon;
  message: string;
  action?: { label: string; onClick: () => void };
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      {Icon && (
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <Icon className="w-6 h-6 text-gray-400" />
        </div>
      )}
      <p className="text-sm text-gray-600 mb-3">{message}</p>
      {action && (
        <Button size="sm" variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
};

/**
 * Inline Empty State for lists
 */
export const InlineEmptyState = ({
  icon: Icon,
  message,
}: {
  icon?: LucideIcon;
  message: string;
}) => {
  return (
    <div className="flex items-center justify-center gap-2 py-6 text-gray-500">
      {Icon && <Icon className="w-5 h-5" />}
      <span className="text-sm">{message}</span>
    </div>
  );
};
